import React, { useState, useEffect } from 'react';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { MessageSquare, Save, Loader2, Send, CheckCircle2, XCircle, Copy, ExternalLink, Sliders, FileEdit, RotateCcw, Eye, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { AgendamentosRelatorios } from '../components/config/AgendamentosRelatorios';

// URL dinâmica: reflete o ambiente atual (Preview ou Produção) em vez de um
// domínio fixo — evita divergência quando o app roda em domínios diferentes.
const WEBHOOK_BASE = `${window.location.origin}/api/zapi/webhook`;

export default function ConfigZapi() {
  const [config, setConfig] = useState({
    instance_id: '',
    instance_token: '',
    client_token: '',
    business_phone_number: '',
    is_active: false,
  });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testText, setTestText] = useState('Teste de integração Nautilus OS - Z-API funcionando!');

  // Regras de disparo (Compras) e templates editáveis (Z-API Fase 2)
  const [regraCompras, setRegraCompras] = useState({
    cotacao_primeira_resposta_ativo: true,
    cotacao_regra_disparo: 'primeira_resposta',
    margem_critica_ativo: true,
  });
  const [savingRegra, setSavingRegra] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templateSelecionado, setTemplateSelecionado] = useState('cotacao_resposta_responsavel');
  const [templateTexto, setTemplateTexto] = useState('');
  const [templateCabecalho, setTemplateCabecalho] = useState('');
  const [templateRodape, setTemplateRodape] = useState('');
  const [templateAtivo, setTemplateAtivo] = useState(true);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTexto, setPreviewTexto] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    loadConfig();
    loadRegraCompras();
    loadTemplates();
  }, []);

  const loadRegraCompras = async () => {
    try {
      const { data } = await api.get('/configuracoes/alertas-compras');
      setRegraCompras({
        cotacao_primeira_resposta_ativo: data.cotacao_primeira_resposta_ativo ?? true,
        cotacao_regra_disparo: data.cotacao_regra_disparo || 'primeira_resposta',
        margem_critica_ativo: data.margem_critica_ativo ?? true,
      });
    } catch (e) {
      // mantém defaults
    }
  };

  const handleSaveRegraCompras = async () => {
    try {
      setSavingRegra(true);
      await api.put('/configuracoes/alertas-compras', regraCompras);
      toast.success('Regra de disparo salva');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar regra');
    } finally {
      setSavingRegra(false);
    }
  };

  const loadTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const { data } = await api.get('/zapi/templates');
      setTemplates(data || []);
      const atual = (data || []).find((t) => t.tipo === templateSelecionado);
      if (atual) {
        setTemplateTexto(atual.texto || '');
        setTemplateCabecalho(atual.cabecalho || '');
        setTemplateRodape(atual.rodape || '');
        setTemplateAtivo(atual.ativo ?? true);
      }
    } catch (e) {
      toast.error('Erro ao carregar templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleSelecionarTemplate = (tipo) => {
    setTemplateSelecionado(tipo);
    const t = templates.find((x) => x.tipo === tipo);
    setTemplateTexto(t?.texto || '');
    setTemplateCabecalho(t?.cabecalho || '');
    setTemplateRodape(t?.rodape || '');
    setTemplateAtivo(t?.ativo ?? true);
  };

  const handleSaveTemplate = async () => {
    const isRelatorio = templateAtual?.formato === 'relatorio';
    if (isRelatorio) {
      if (!templateCabecalho.trim() || !templateRodape.trim()) {
        toast.error('Cabeçalho e Rodapé não podem ser vazios');
        return;
      }
    } else if (!templateTexto.trim()) {
      toast.error('O texto do template não pode ser vazio');
      return;
    }
    try {
      setSavingTemplate(true);
      const payload = isRelatorio
        ? { cabecalho: templateCabecalho, rodape: templateRodape, ativo: templateAtivo }
        : { texto: templateTexto, ativo: templateAtivo };
      await api.put(`/zapi/templates/${templateSelecionado}`, payload);
      toast.success('Template salvo');
      await loadTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handlePreviewTemplate = async () => {
    const isRelatorio = templateAtual?.formato === 'relatorio';
    try {
      setPreviewLoading(true);
      setPreviewOpen(true);
      const payload = isRelatorio
        ? { cabecalho: templateCabecalho, rodape: templateRodape }
        : { texto: templateTexto };
      const { data } = await api.post(`/zapi/templates/${templateSelecionado}/preview`, payload);
      setPreviewTexto(data.preview || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao gerar visualização');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };


  const handleResetTemplate = async () => {
    try {
      await api.delete(`/zapi/templates/${templateSelecionado}`);
      toast.success('Template restaurado ao padrão');
      await loadTemplates();
    } catch (e) {
      toast.error('Erro ao restaurar template');
    }
  };

  const templateAtual = templates.find((t) => t.tipo === templateSelecionado);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/zapi/config');
      setConfig({
        instance_id: data.instance_id || '',
        instance_token: data.instance_token || '',
        client_token: data.client_token || '',
        business_phone_number: data.business_phone_number || '',
        is_active: !!data.is_active,
      });
      await loadStatus();
    } catch (e) {
      toast.error('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };

  const loadStatus = async () => {
    try {
      const { data } = await api.get('/zapi/status');
      setStatus(data);
    } catch (e) {
      setStatus({ ok: false, error: 'não foi possível consultar' });
    }
  };

  const handleSave = async () => {
    if (!config.instance_id || !config.instance_token) {
      toast.error('Informe Instance ID e Instance Token');
      return;
    }
    try {
      setSaving(true);
      await api.put('/zapi/config', config);
      toast.success('Configuração salva');
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testPhone) {
      toast.error('Informe o telefone destino');
      return;
    }
    try {
      setTesting(true);
      const { data } = await api.post('/zapi/test-send', { to: testPhone, text: testText });
      toast.success(`Enviado! ID: ${data.message_id || 'ok'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha no envio');
    } finally {
      setTesting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('URL copiada');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const isConnected = status?.data?.connected === true;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="config-zapi-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <MessageSquare className="w-7 h-7 text-emerald-600" />
            Z-API (WhatsApp)
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Provedor para notificações internas via WhatsApp. Complementa a integração Meta Cloud.
          </p>
        </div>
        {status?.configured && (
          <Badge
            data-testid="zapi-status-badge"
            className={isConnected ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}
          >
            {isConnected ? (
              <><CheckCircle2 className="w-3 h-3 mr-1" /> Conectado</>
            ) : (
              <><XCircle className="w-3 h-3 mr-1" /> Desconectado</>
            )}
          </Badge>
        )}
      </div>

      {/* Credenciais */}
      <Tabs defaultValue="configuracao" className="w-full">
        <TabsList data-testid="zapi-tabs-list">
          <TabsTrigger value="configuracao" data-testid="zapi-tab-configuracao">Configuração</TabsTrigger>
          <TabsTrigger value="regras-templates" data-testid="zapi-tab-regras-templates">Regras &amp; Templates</TabsTrigger>
          <TabsTrigger value="agendamentos" data-testid="zapi-tab-agendamentos">Agendamentos</TabsTrigger>
        </TabsList>

        <TabsContent value="configuracao" className="space-y-6 mt-4">
      <Card>
        <CardHeader>
          <CardTitle>Credenciais</CardTitle>
          <CardDescription>
            Obtenha em <a href="https://app.z-api.io" target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline">app.z-api.io</a> → Instância + Conta → Segurança
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Instance ID</Label>
            <Input
              data-testid="zapi-instance-id"
              value={config.instance_id}
              onChange={(e) => setConfig({ ...config, instance_id: e.target.value.trim() })}
              placeholder="3F62AF41F930226AEE55CE211525A05E"
              className="font-mono"
            />
          </div>
          <div>
            <Label>Instance Token</Label>
            <Input
              data-testid="zapi-instance-token"
              type="password"
              value={config.instance_token}
              onChange={(e) => setConfig({ ...config, instance_token: e.target.value.trim() })}
              placeholder="Token da instância"
              className="font-mono"
            />
          </div>
          <div>
            <Label>Client-Token (Segurança da conta)</Label>
            <Input
              data-testid="zapi-client-token"
              type="password"
              value={config.client_token}
              onChange={(e) => setConfig({ ...config, client_token: e.target.value.trim() })}
              placeholder="Account Security Token (obrigatório)"
              className="font-mono"
            />
            <p className="text-xs text-slate-500 mt-1">
              Painel Z-API → menu <strong>Conta</strong> → <strong>Segurança da conta</strong> → copiar Account Security Token.
            </p>
          </div>
          <div>
            <Label>Número do WhatsApp Business (referência)</Label>
            <Input
              data-testid="zapi-business-phone"
              value={config.business_phone_number}
              onChange={(e) => setConfig({ ...config, business_phone_number: e.target.value })}
              placeholder="+55 21 99999-0000"
            />
          </div>
          <div className="flex items-center justify-between pt-2 border-t">
            <div>
              <Label className="text-base">Integração ativa</Label>
              <p className="text-xs text-slate-500">
                Quando desligada, o serviço de notificação ignora envio pelo Z-API.
              </p>
            </div>
            <Switch
              data-testid="zapi-active-switch"
              checked={config.is_active}
              onCheckedChange={(v) => setConfig({ ...config, is_active: v })}
            />
          </div>
          <Button
            data-testid="zapi-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="w-full sm:w-auto"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Salvar configuração
          </Button>
        </CardContent>
      </Card>

      {/* URLs de Webhook */}
      <Card>
        <CardHeader>
          <CardTitle>URLs de Webhook</CardTitle>
          <CardDescription>
            Cole cada URL no painel Z-API (menu <strong>Configurar Webhooks</strong>). Ver instruções detalhadas de quais toggles ligar/desligar no manual.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {[
            { label: 'Ao conectar', path: '/connect', desc: 'Instância reconectou' },
            { label: 'Ao desconectar', path: '/disconnect', desc: 'Instância caiu (dispara alerta pros admins)' },
            { label: 'Receber status da mensagem', path: '/status', desc: 'Entregue / Lido (rastreamento)' },
            { label: 'Ao receber (Fase 2)', path: '/receive', desc: 'Manter "Ignorar" ligado por enquanto' },
          ].map((w) => (
            <div key={w.path} className="flex items-center gap-2 p-3 bg-slate-50 rounded border">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{w.label}</div>
                <div className="font-mono text-xs text-slate-600 truncate">{WEBHOOK_BASE}{w.path}</div>
                <div className="text-xs text-slate-500 mt-0.5">{w.desc}</div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(`${WEBHOOK_BASE}${w.path}`)}
                data-testid={`copy-webhook-${w.path.slice(1)}`}
              >
                <Copy className="w-3 h-3" />
              </Button>
            </div>
          ))}
          <a
            href="https://app.z-api.io"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-emerald-600 hover:underline mt-2"
          >
            Abrir painel Z-API <ExternalLink className="w-3 h-3" />
          </a>
        </CardContent>
      </Card>

      {/* Teste de envio */}
      <Card>
        <CardHeader>
          <CardTitle>Teste de envio</CardTitle>
          <CardDescription>
            Envie uma mensagem real para validar. Formato aceito: qualquer variação (com/sem DDI, com/sem parênteses).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Telefone destino</Label>
            <Input
              data-testid="zapi-test-phone"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="(21) 99988-7766 ou 5521999887766"
            />
          </div>
          <div>
            <Label>Mensagem</Label>
            <Input
              data-testid="zapi-test-text"
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
            />
          </div>
          <Button
            data-testid="zapi-test-send-btn"
            onClick={handleTest}
            disabled={testing || !config.is_active}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Enviar teste
          </Button>
          {!config.is_active && (
            <Alert>
              <AlertDescription className="text-xs">
                Ative a integração acima antes de testar.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="regras-templates" className="space-y-6 mt-4">
      {/* Regras de disparo — Cotações (Fase 2) */}
      <Card data-testid="regras-disparo-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sliders className="w-5 h-5 text-indigo-600" />
            Regras de disparo — Resposta de Cotação
          </CardTitle>
          <CardDescription>
            Define quando o Responsável pela cotação é avisado (in-app + WhatsApp) sobre respostas de fornecedores.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between pt-1">
            <div>
              <Label className="text-base">Notificar responsável ao atingir a regra</Label>
              <p className="text-xs text-slate-500">Quando desligado, este gatilho fica pausado (outros alertas continuam normais).</p>
            </div>
            <Switch
              data-testid="regra-cotacao-ativo-switch"
              checked={regraCompras.cotacao_primeira_resposta_ativo}
              onCheckedChange={(v) => setRegraCompras({ ...regraCompras, cotacao_primeira_resposta_ativo: v })}
            />
          </div>
          <div>
            <Label>Regra de disparo</Label>
            <Select
              value={regraCompras.cotacao_regra_disparo}
              onValueChange={(v) => setRegraCompras({ ...regraCompras, cotacao_regra_disparo: v })}
            >
              <SelectTrigger data-testid="regra-cotacao-select" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="primeira_resposta">Na 1ª resposta de fornecedor</SelectItem>
                <SelectItem value="100_porcento">Quando 100% dos fornecedores responderem</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            data-testid="regra-cotacao-save-btn"
            onClick={handleSaveRegraCompras}
            disabled={savingRegra}
            className="w-full sm:w-auto"
          >
            {savingRegra ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Salvar regra
          </Button>
        </CardContent>
      </Card>

      {/* Alertas Financeiros — Margem Crítica (automático, lê a Margem Alvo da aba Financeiro do projeto) */}
      <Card data-testid="alertas-financeiros-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Alertas Financeiros
          </CardTitle>
          <CardDescription>
            Sem configuração de prazo/critério aqui: o alerta lê automaticamente a Margem Alvo definida na aba Financeiro de cada projeto
            (ou usa 15% como padrão do sistema, se o projeto não definir a sua própria meta).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Ativar aviso de Margem Crítica</Label>
              <p className="text-xs text-slate-500">
                Avisa o Papel "Financeiro" quando a margem real de um projeto cair perto ou abaixo da meta.
              </p>
            </div>
            <Switch
              data-testid="margem-critica-ativo-switch"
              checked={regraCompras.margem_critica_ativo}
              onCheckedChange={async (v) => {
                const novo = { ...regraCompras, margem_critica_ativo: v };
                setRegraCompras(novo);
                try {
                  await api.put('/configuracoes/alertas-compras', novo);
                  toast.success(v ? 'Aviso de Margem Crítica ativado' : 'Aviso de Margem Crítica desativado');
                } catch (e) {
                  toast.error('Erro ao salvar');
                }
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Templates editáveis de WhatsApp */}
      <Card data-testid="templates-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileEdit className="w-5 h-5 text-emerald-600" />
            Templates de Mensagem
          </CardTitle>
          <CardDescription>
            Personalize o texto enviado no WhatsApp para cada tipo de alerta. Use as variáveis entre chaves — elas são substituídas automaticamente.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {templatesLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : (
            <>
              <div>
                <Label>Tipo de alerta</Label>
                <Select value={templateSelecionado} onValueChange={handleSelecionarTemplate}>
                  <SelectTrigger data-testid="template-select" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.tipo} value={t.tipo}>
                        {t.tipo} · {t.formato === 'relatorio' ? 'relatório' : 'instantâneo'} {t.personalizado ? '· personalizado' : '· padrão'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {templateAtual?.descricao && (
                  <p className="text-xs text-slate-500 mt-1">{templateAtual.descricao}</p>
                )}
                {templateAtual?.variaveis && (
                  <p className="text-xs font-mono text-indigo-600 mt-1">{templateAtual.variaveis}</p>
                )}
              </div>
              {templateAtual?.formato === 'relatorio' ? (
                <>
                  <div>
                    <Label>Cabeçalho</Label>
                    <p className="text-xs text-slate-500 mb-1">Texto exibido ANTES da lista dinâmica gerada pelo sistema (ex: saudação).</p>
                    <Textarea
                      data-testid="template-cabecalho-input"
                      value={templateCabecalho}
                      onChange={(e) => setTemplateCabecalho(e.target.value)}
                      rows={4}
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    <Label>Rodapé</Label>
                    <p className="text-xs text-slate-500 mb-1">Texto exibido DEPOIS da lista dinâmica (ex: link + assinatura).</p>
                    <Textarea
                      data-testid="template-rodape-input"
                      value={templateRodape}
                      onChange={(e) => setTemplateRodape(e.target.value)}
                      rows={3}
                      className="font-mono text-sm"
                    />
                  </div>
                </>
              ) : (
                <div>
                  <Label>Texto da mensagem</Label>
                  <Textarea
                    data-testid="template-texto-input"
                    value={templateTexto}
                    onChange={(e) => setTemplateTexto(e.target.value)}
                    rows={7}
                    className="font-mono text-sm mt-1"
                  />
                </div>
              )}
              <div className="flex items-center justify-between pt-1 border-t">
                <div>
                  <Label className="text-base">Template ativo</Label>
                  <p className="text-xs text-slate-500">Quando desligado, usa o texto padrão do sistema em vez deste.</p>
                </div>
                <Switch
                  data-testid="template-ativo-switch"
                  checked={templateAtivo}
                  onCheckedChange={setTemplateAtivo}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid="template-save-btn"
                  onClick={handleSaveTemplate}
                  disabled={savingTemplate}
                >
                  {savingTemplate ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Salvar template
                </Button>
                <Button
                  data-testid="template-preview-btn"
                  variant="outline"
                  onClick={handlePreviewTemplate}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Visualizar Exemplo
                </Button>
                {templateAtual?.personalizado && (
                  <Button
                    data-testid="template-reset-btn"
                    variant="outline"
                    onClick={handleResetTemplate}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Restaurar padrão
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="agendamentos" className="mt-4">
      {/* Agendamentos de Relatórios (Central de Notificações Z-API) */}
      <AgendamentosRelatorios />
        </TabsContent>
      </Tabs>

      {/* Preview do template com dados fictícios ("Visualizar Exemplo") */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent data-testid="template-preview-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Visualizar Exemplo</DialogTitle>
            <DialogDescription>
              Como a mensagem ficará no WhatsApp (com dados fictícios nas variáveis) — ainda não foi enviada nem salva.
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="bg-[#e5ddd5] rounded-lg p-4">
              <div
                data-testid="template-preview-bubble"
                className="bg-[#dcf8c6] rounded-lg rounded-tr-none p-3 text-sm text-slate-800 whitespace-pre-wrap shadow-sm max-w-[90%] ml-auto"
                style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}
              >
                {previewTexto}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
